import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient, type QueryKey, type UseQueryOptions } from '@tanstack/react-query';
import { ApiError, get, onSessionEnded, post, setApiBusiness } from './api.ts';
import type { CurrentBusiness, Me } from './types.ts';
import type { Permission } from '../../shared/permissions.ts';

const STORAGE_KEY = 'lb.business';

function readStored(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStored(id: string | null) {
  try {
    if (id) localStorage.setItem(STORAGE_KEY, id);
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* private mode: selection simply isn't remembered */
  }
}

interface SessionValue {
  me: Me | null;
  loading: boolean;
  businessId: string | null;
  selectBusiness: (id: string | null) => void;
  refreshMe: () => Promise<unknown>;
  /** Signs out this device, or every device, and forgets everything loaded for the account. */
  signOut: (everywhere?: boolean) => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        return await get<Me>('/me');
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 5 * 60_000,
    retry: (count, e) => !(e instanceof ApiError && e.status === 401) && count < 2,
  });

  const [selected, setSelected] = useState<string | null>(() => readStored());

  const me = meQuery.data ?? null;
  // Fall back to the first business when the stored one isn't ours (any more).
  const businessId = useMemo(() => {
    if (!me) return null;
    if (selected && me.businesses.some((b) => b.id === selected)) return selected;
    return me.businesses[0]?.id ?? null;
  }, [me, selected]);

  setApiBusiness(businessId);

  const selectBusiness = useCallback((id: string | null) => {
    // Never show one business's cached data under another.
    qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'me' });
    setApiBusiness(id);
    writeStored(id);
    setSelected(id);
  }, [qc]);

  useEffect(() => {
    onSessionEnded(() => {
      qc.setQueryData(['me'], null);
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'me' });
    });
  }, [qc]);

  const signOut = useCallback(async (everywhere = false) => {
    await post(everywhere ? '/me/logout-everywhere' : '/auth/logout').catch(() => {});
    // Mark the session ended on the query the app is watching, then drop the rest.
    // (Clearing the whole cache would detach the app from it and leave it on the loading screen.)
    qc.setQueryData(['me'], null);
    qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'me' });
  }, [qc]);

  const value = useMemo<SessionValue>(() => ({
    me,
    loading: meQuery.isPending,
    businessId,
    selectBusiness,
    refreshMe: () => meQuery.refetch(),
    signOut,
  }), [me, meQuery.isPending, businessId, selectBusiness, meQuery, signOut]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
  const v = useContext(SessionContext);
  if (!v) throw new Error('useSession outside SessionProvider');
  return v;
}

// ───────────────────────── current business ─────────────────────────

interface BusinessValue extends CurrentBusiness {
  can: (p: Permission) => boolean;
  canAny: (...p: Permission[]) => boolean;
  refresh: () => Promise<unknown>;
}

const BusinessContext = createContext<BusinessValue | null>(null);

export function useBusinessQuery() {
  const { businessId } = useSession();
  return useQuery({
    queryKey: ['biz', businessId, 'current'],
    queryFn: () => get<CurrentBusiness>('/businesses/current'),
    enabled: !!businessId,
    staleTime: 60_000,
  });
}

export function BusinessProvider({ data, refresh, children }: { data: CurrentBusiness; refresh: () => Promise<unknown>; children: ReactNode }) {
  const value = useMemo<BusinessValue>(() => {
    const perms = new Set(data.me.permissions);
    const can = (p: Permission) => data.me.isOwner || perms.has(p);
    return { ...data, can, canAny: (...ps) => ps.some(can), refresh };
  }, [data, refresh]);
  return <BusinessContext.Provider value={value}>{children}</BusinessContext.Provider>;
}

export function useBiz() {
  const v = useContext(BusinessContext);
  if (!v) throw new Error('useBiz outside BusinessProvider');
  return v;
}

/** Query scoped to the current business; the key always includes its id. */
export function useBizQuery<T>(key: QueryKey, path: string | null, opts: Partial<UseQueryOptions<T, Error, T, QueryKey>> = {}) {
  const { businessId } = useSession();
  return useQuery<T, Error, T, QueryKey>({
    ...opts,
    queryKey: ['biz', businessId, ...(key as unknown[])],
    queryFn: ({ signal }) => get<T>(path!, signal),
    enabled: !!businessId && !!path && (opts.enabled ?? true),
  } as UseQueryOptions<T, Error, T, QueryKey>);
}

/** Invalidate everything for the current business after a change. */
export function useInvalidate() {
  const qc = useQueryClient();
  const { businessId } = useSession();
  return useCallback((...prefixes: string[]) => {
    if (!prefixes.length) return qc.invalidateQueries({ queryKey: ['biz', businessId] });
    return Promise.all(prefixes.map((p) => qc.invalidateQueries({ queryKey: ['biz', businessId, p] })));
  }, [qc, businessId]);
}
